import {
  routeForBenchmarks,
  routeForDataGovernance,
  routeForFootprintSuppliers,
  routeForFormalReporting,
  routeForMarketplace,
  routeForMeasureProjects,
  routeForMeasurementProject,
  routeForReduxEnterprise,
  routeForRenewableEnergy,
  routeForFormalReport,
} from '@watershed/shared-universal/dashboardRoutes';
import {
  GQClimateProgramTimelineProjectFieldsFragment,
  GQLinkedMeasurementFieldsFragment,
  GQLinkedReportFieldsFragment,
} from '@watershed/app-dashboard/generated/graphql-operations';
import { GQClimateProgramTimelineProjectObjectType } from '@watershed/app-dashboard/generated/graphql-schema-types';

import {
  HAS_ACCESS_TO_CLEAN_POWER,
  HAS_ACCESS_TO_DATA_GOVERNANCE,
  HAS_ACCESS_TO_FORMAL_REPORTING,
  HAS_ACCESS_TO_MEASUREMENT_PROJECTS,
  HAS_ACCESS_TO_SUPPLY_CHAIN,
  HAS_ACCESS_TO_REDUCTION_PLANS,
  type FeatureAccessCheckDescriptor,
} from '../../utils/featureAccess';
import assertNever from '@watershed/shared-util/assertNever';
import {
  isRouteType,
  RouteEnumType,
} from '@watershed/shared-universal/climateProgram/projects';

import { ClimateProgramTimelineProject } from './ClimateProgramTimelineItem';

interface CtaInfo {
  href: string;
  accessCheckDescriptor: FeatureAccessCheckDescriptor | null;
  label: string;
}
const ROUTE_ENUM_TO_CTA: Record<RouteEnumType, CtaInfo> = {
  Benchmarks: {
    href: routeForBenchmarks(),
    label: 'View benchmarks',
    accessCheckDescriptor: null,
  },
  CarbonAvoidance: {
    href: routeForMarketplace(),
    label: 'View avoidance projects',
    accessCheckDescriptor: null,
  },
  CarbonRemoval: {
    href: routeForMarketplace(),
    label: 'View removal projects',
    accessCheckDescriptor: null,
  },
  CleanPower: {
    href: routeForRenewableEnergy(),
    label: 'Clean power options',
    accessCheckDescriptor: HAS_ACCESS_TO_CLEAN_POWER,
  },
  SupplyChain: {
    href: routeForFootprintSuppliers(),
    label: 'View program',
    accessCheckDescriptor: HAS_ACCESS_TO_SUPPLY_CHAIN,
  },
  VerifyFootprint: {
    href: routeForDataGovernance(),
    label: 'View data governance',
    accessCheckDescriptor: HAS_ACCESS_TO_DATA_GOVERNANCE,
  },
  ViewReductionPlans: {
    href: routeForReduxEnterprise(),
    label: 'View reduction plan',
    accessCheckDescriptor: HAS_ACCESS_TO_REDUCTION_PLANS,
  },
  Reports: {
    href: routeForFormalReporting({ page: 'reports' }),
    label: 'View reports',
    accessCheckDescriptor: HAS_ACCESS_TO_FORMAL_REPORTING,
  },
  Measurements: {
    href: routeForMeasureProjects(),
    label: 'View measurements',
    accessCheckDescriptor: HAS_ACCESS_TO_MEASUREMENT_PROJECTS,
  },
};

export function getCtaInfoForCanonicalClimateProgramProject(
  canonicalClimateProgramProject: {
    id: string;
    routeType: string | null;
  } | null
): CtaInfo | undefined {
  const routeTypeFromObject = canonicalClimateProgramProject?.routeType ?? null;
  if (isRouteType(routeTypeFromObject)) {
    return ROUTE_ENUM_TO_CTA[routeTypeFromObject];
  } else {
    return undefined;
  }
}

export type LinkedReportTimelineProject =
  GQClimateProgramTimelineProjectFieldsFragment & {
    object: GQLinkedReportFieldsFragment;
  };

export type LinkedMeasurementTimelineProject =
  GQClimateProgramTimelineProjectFieldsFragment & {
    object: GQLinkedMeasurementFieldsFragment;
  };

export function getCtaInfoForLinkedTimelineReportItem(
  linkedReport: GQLinkedReportFieldsFragment
): CtaInfo {
  return {
    href: routeForFormalReport({
      reportId: linkedReport.id,
      config: linkedReport.config,
    }),
    label: `View ${linkedReport.name}`,
    accessCheckDescriptor: HAS_ACCESS_TO_FORMAL_REPORTING,
  };
}

export function getCtaInfoForTimelineItem(
  timelineItem:
    | ClimateProgramTimelineProject
    | GQClimateProgramTimelineProjectFieldsFragment
): CtaInfo | undefined {
  const linkedObjectType = timelineItem.object.__typename;
  switch (linkedObjectType) {
    case GQClimateProgramTimelineProjectObjectType.ClimateProgramProject:
      return getCtaInfoForCanonicalClimateProgramProject(
        timelineItem.canonicalClimateProgramProject
      );
    case GQClimateProgramTimelineProjectObjectType.MeasurementProject:
      const linkedMeasurement = timelineItem.object;
      return {
        href: routeForMeasurementProject(linkedMeasurement.id),
        label: `View ${linkedMeasurement.name}`,
        accessCheckDescriptor: HAS_ACCESS_TO_MEASUREMENT_PROJECTS,
      };
    case GQClimateProgramTimelineProjectObjectType.Report:
      return getCtaInfoForLinkedTimelineReportItem(timelineItem.object);
    default:
      assertNever(linkedObjectType);
  }
}
